/* ═══════════════════════════════════════════════════════════════════════════
   CUSTOMERS REPORT — SHARED UI PRIMITIVES
   Reused by the dashboard (CustomersReport.tsx) and the drill/profile views
   (customersReportViews.tsx). Styling follows the sibling LoyaltyReport: inline
   T-token styles + the same InfoTip (CUST-0.1 mirrors LOY-RPT-P-0.1).
   ═══════════════════════════════════════════════════════════════════════════ */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { CSSProperties, MouseEvent as ReactMouseEvent, ReactNode, RefObject } from "react";
import { createPortal } from "react-dom";
import { T, useIsDesktop } from "./reportTokens";
import { useCurrencySymbol } from "../../utils/currency";

/* ─── Icon ────────────────────────────────────────────────────────────────── */
export function Icon({ name, size = 20, color, fill = 0, weight = 400, style = {}, onClick, title }: any) {
    return (
        <span
            className="material-symbols-rounded"
            onClick={onClick}
            title={title}
            style={{
                fontSize: size, color, lineHeight: 1, verticalAlign: "middle", userSelect: "none",
                fontVariationSettings: `'FILL' ${fill}, 'wght' ${weight}, 'opsz' ${size}`,
                ...(onClick ? { cursor: "pointer" } : {}),
                ...style,
            }}
        >
            {name}
        </span>
    );
}

/* ─── Currency formatter (reports-currency-by-country) ─────────────────────── */
export function useMoney() {
    const sym = useCurrencySymbol();
    return (n: number | null | undefined, opts: { decimals?: boolean } = {}) => {
        if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
        const v = Number(n);
        const body = opts.decimals
            ? v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : Math.round(v).toLocaleString();
        return sym ? `${sym}${body}` : body;
    };
}

/* ─── InfoTip (CUST-0.1) — portal bubble, hover on desktop / tap on mobile ─── */
const INFO_EVENT = "cust-info-open";
let _infoSeq = 0;
const INFO_TIP_W = 240;

type TipPos = { left: number; top: number; place: "top" | "bottom"; caretLeft: number };

/**
 * The bubble behind both InfoTip and HoverTip: portalled to <body> so a
 * scrolling table can't clip it, placed above the anchor when there is room
 * (below otherwise), kept inside the viewport, and closed by another tip
 * opening, an outside click, a scroll or a resize.
 */
function useTipBubble(anchorRef: RefObject<HTMLElement>, width: number) {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState<TipPos | null>(null);
    const id = useRef(`info_${_infoSeq++}`).current;
    const bubbleRef = useRef<HTMLDivElement>(null);

    const compute = () => {
        const el = anchorRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const iconCenterX = r.left + r.width / 2;
        const viewportW = document.documentElement.clientWidth;
        const left = Math.max(8, Math.min(iconCenterX - width / 2, viewportW - width - 8));
        const place: "top" | "bottom" = r.top > 170 ? "top" : "bottom";
        setPos({ left, top: place === "top" ? r.top - 8 : r.bottom + 8, place, caretLeft: iconCenterX - left });
    };

    const show = () => {
        window.dispatchEvent(new CustomEvent(INFO_EVENT, { detail: id }));
        compute();
        setOpen(true);
    };

    useLayoutEffect(() => {
        if (!open || !anchorRef.current || !bubbleRef.current) return;
        const r = anchorRef.current.getBoundingClientRect();
        const b = bubbleRef.current.getBoundingClientRect();
        const iconCenterX = r.left + r.width / 2;
        const viewportW = document.documentElement.clientWidth;
        const left = Math.max(8, Math.min(iconCenterX - b.width / 2, viewportW - b.width - 8));
        const place: "top" | "bottom" = r.top - b.height - 8 > 8 ? "top" : "bottom";
        setPos({ left, top: place === "top" ? r.top - 8 : r.bottom + 8, place, caretLeft: iconCenterX - left });
    }, [open, anchorRef]);

    useEffect(() => {
        if (!open) return;
        const onOther = (e: Event) => { if ((e as CustomEvent).detail !== id) setOpen(false); };
        const onOutside = (e: MouseEvent) => { if (anchorRef.current && !anchorRef.current.contains(e.target as Node)) setOpen(false); };
        const onMove = () => setOpen(false);
        window.addEventListener(INFO_EVENT, onOther);
        document.addEventListener("mousedown", onOutside);
        window.addEventListener("scroll", onMove, true);
        window.addEventListener("resize", onMove);
        return () => {
            window.removeEventListener(INFO_EVENT, onOther);
            document.removeEventListener("mousedown", onOutside);
            window.removeEventListener("scroll", onMove, true);
            window.removeEventListener("resize", onMove);
        };
    }, [open, id, anchorRef]);

    const render = (content: ReactNode) => open && pos ? createPortal(
        <div
            ref={bubbleRef}
            style={{
                position: "fixed", zIndex: 4000, left: pos.left, top: pos.top,
                transform: pos.place === "top" ? "translateY(-100%)" : "none",
                width, maxWidth: "calc(100vw - 16px)",
                background: T.surface, color: T.t2, border: `1px solid ${T.cardBorder}`,
                fontSize: 11.5, lineHeight: 1.55, fontWeight: 400, padding: "10px 12px", borderRadius: 10,
                boxShadow: T.popoverShadow,
                textTransform: "none", letterSpacing: 0, whiteSpace: "normal", overflowWrap: "anywhere", pointerEvents: "none",
            }}
        >
            {content}
            <div style={{
                position: "absolute", [pos.place === "top" ? "bottom" : "top"]: -5, left: pos.caretLeft,
                transform: "translateX(-50%) rotate(45deg)", width: 8, height: 8, background: T.surface,
                ...(pos.place === "top"
                    ? { borderRight: `1px solid ${T.cardBorder}`, borderBottom: `1px solid ${T.cardBorder}` }
                    : { borderLeft: `1px solid ${T.cardBorder}`, borderTop: `1px solid ${T.cardBorder}` }),
            }} />
        </div>,
        document.body
    ) : null;

    return { open, setOpen, show, render };
}

/**
 * Hover tooltip for text that may be cut short - the report's own bubble
 * instead of the browser's native `title` (QA #14). Opens on desktop hover only
 * when the content really is truncated, so a short value never shows a tooltip
 * repeating itself. Mobile gets nothing: there a tap opens the row instead.
 */
export function HoverTip({ text, children, width = 320, style = {} }: {
    text: string;
    children: ReactNode;
    width?: number;
    style?: CSSProperties;
}) {
    const isDesktop = useIsDesktop();
    const ref = useRef<HTMLSpanElement>(null);
    const tip = useTipBubble(ref, width);
    const isClipped = () => {
        const el = ref.current;
        return !!el && (el.scrollHeight > el.clientHeight + 1 || el.scrollWidth > el.clientWidth + 1);
    };
    return (
        <span
            ref={ref}
            onMouseEnter={isDesktop ? () => { if (isClipped()) tip.show(); } : undefined}
            onMouseLeave={isDesktop ? () => tip.setOpen(false) : undefined}
            style={style}
        >
            {children}
            {tip.render(text)}
        </span>
    );
}

/**
 * Detail tooltip for a data point (a cohort cell, a caution icon) - the report's
 * own bubble on BOTH layouts, which the native `title` can't do: a phone has no
 * hover, so a `title` never shows in the owner app (QA #22). Desktop opens it on
 * hover; mobile on tap, and a second tap, a tap elsewhere, another tip opening
 * or a scroll closes it.
 */
export function TapTip({ content, children, width = 220, inline = false, style = {} }: {
    content: ReactNode;
    children: ReactNode;
    width?: number;
    /** Render as an inline `span` (an icon beside text) instead of a block `div` (a grid cell). */
    inline?: boolean;
    style?: CSSProperties;
}) {
    const isDesktop = useIsDesktop();
    const ref = useRef<HTMLElement>(null);
    const tip = useTipBubble(ref, width);
    const props = {
        ref: ref as any,
        onMouseEnter: isDesktop ? tip.show : undefined,
        onMouseLeave: isDesktop ? () => tip.setOpen(false) : undefined,
        onClick: isDesktop ? undefined : (e: ReactMouseEvent) => { e.stopPropagation(); if (tip.open) tip.setOpen(false); else tip.show(); },
        style: { cursor: isDesktop ? "default" : "pointer", ...style },
    };
    return inline
        ? <span {...props}>{children}{tip.render(content)}</span>
        : <div {...props}>{children}{tip.render(content)}</div>;
}

export function InfoTip({ text }: { text: string }) {
    const isDesktop = useIsDesktop();
    const ref = useRef<HTMLSpanElement>(null);
    const { open, setOpen, show, render } = useTipBubble(ref, INFO_TIP_W);
    const bubble = render(text);

    return (
        <span
            ref={ref}
            onMouseEnter={isDesktop ? show : undefined}
            onMouseLeave={isDesktop ? () => setOpen(false) : undefined}
            onClick={(e) => { e.stopPropagation(); if (!isDesktop) (open ? setOpen(false) : show()); else show(); }}
            style={{ position: "relative", display: "inline-flex", alignItems: "center", cursor: "pointer", marginLeft: 4, verticalAlign: "middle" }}
        >
            <span className="material-symbols-rounded" style={{ fontSize: 14, color: T.t3, opacity: 0.85 }}>info</span>
            {bubble}
        </span>
    );
}

/* ─── Section card ────────────────────────────────────────────────────────── */
export function SectionCard({ title, subtitle, info, icon, right, children, id, style = {} }: any) {
    const isDesktop = useIsDesktop();
    return (
        <div id={id} style={{ background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: isDesktop ? 22 : 14, boxShadow: T.cardShadow, ...style }}>
            {(title || right) && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                        {icon && <Icon name={icon} size={isDesktop ? 18 : 15} color={T.t3} weight={400} />}
                        <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: isDesktop ? 15 : 13, fontWeight: 700, color: T.t1, display: "flex", alignItems: "center" }}>
                                {title}{info && <InfoTip text={info} />}
                            </div>
                            {subtitle && <div style={{ fontSize: isDesktop ? 11 : 10, color: T.t3, marginTop: 2 }}>{subtitle}</div>}
                        </div>
                    </div>
                    {right}
                </div>
            )}
            {children}
        </div>
    );
}

/* ─── Segment badge ───────────────────────────────────────────────────────── */
export function SegmentBadge({ label, color }: { label: string; color: string }) {
    return (
        <span style={{
            display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px",
            borderRadius: 4, fontSize: 10, fontWeight: 600, color, background: `${color}14`, whiteSpace: "nowrap",
        }}>
            <span style={{ width: 6, height: 6, borderRadius: 2, background: color }} />
            {label}
        </span>
    );
}

/* ─── Avatar (initials, segment-colored) ──────────────────────────────────── */
export function Avatar({ name, color, size = 28 }: { name: string; color: string; size?: number }) {
    const initials = name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase() || "—";
    return (
        <div style={{
            width: size, height: size, borderRadius: "50%", flexShrink: 0,
            background: `${color}18`, color, display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: size * 0.4, fontWeight: 600,
        }}>{initials}</div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   PAGINATION PRIMITIVES
   Shared by the Customer Directory (CUST-8.1) and every drill-in customer table
   (CUST-5.1 / 6.x), so paging behaves identically wherever customers are listed.
   ═══════════════════════════════════════════════════════════════════════════ */
export const PAGE_SIZES = [10, 25, 50, 100];

/** Sentinel page size meaning "no paging — return/render every row". */
export const ALL_ROWS = 0;

/**
 * Rows-per-page control for every customer table - the Directory, each drill and
 * the customer page's Order Log (QA #57).
 *
 * A native `<select>` opens the browser's own grey-and-blue list, which looks
 * like nothing else in the report. This is the report's own dropdown instead:
 * a trigger that reads like the other controls and a popover list with the
 * active size in the accent colour and the Reports hover tint, the same
 * treatment the Snapshot / Offers / Labor pagers give their "Rows per page".
 * Click outside or Escape closes it; the options are real buttons, so Tab and
 * Enter work. One component for every customer table, so they cannot drift.
 */
export function PageSizeSelect({ value, onChange, options = PAGE_SIZES, allowAll = true, totalCount }: {
    value: number;
    onChange: (n: number) => void;
    options?: number[];
    allowAll?: boolean;
    /** When supplied, the All option shows the row count it will render. */
    totalCount?: number;
}) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    const label = (n: number) =>
        n === ALL_ROWS
            ? (totalCount != null ? `All (${totalCount.toLocaleString()})` : "All")
            : `${n} / page`;
    const choices = allowAll ? [...options, ALL_ROWS] : options;

    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    return (
        <div ref={ref} style={{ position: "relative", display: "inline-flex" }}>
            <button
                type="button"
                aria-haspopup="listbox"
                aria-expanded={open}
                onClick={() => setOpen((o) => !o)}
                style={{
                    display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 8px 6px 10px", borderRadius: 8,
                    border: `1px solid ${open ? T.accent : T.cardBorder}`, background: T.surface, fontSize: 12, fontWeight: 500,
                    color: T.t2, fontFamily: T.font, cursor: "pointer", whiteSpace: "nowrap",
                }}
            >
                {label(value)}
                <Icon name={open ? "expand_less" : "expand_more"} size={16} color={T.t3} />
            </button>
            {open && (
                <div
                    role="listbox"
                    style={{
                        position: "absolute", top: "calc(100% + 4px)", left: 0, minWidth: "100%", zIndex: 500, padding: 4,
                        background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 8,
                        boxShadow: T.popoverShadow,
                    }}
                >
                    {choices.map((n) => {
                        const active = n === value;
                        return (
                            <button
                                key={n}
                                type="button"
                                role="option"
                                aria-selected={active}
                                onClick={() => { onChange(n); setOpen(false); }}
                                onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = T.hover; }}
                                onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}
                                style={{
                                    display: "block", width: "100%", textAlign: "left", padding: "7px 10px", border: "none", borderRadius: 6,
                                    background: active ? T.accentSoft : "transparent", fontSize: 12, fontWeight: active ? 700 : 500,
                                    color: active ? T.accent : T.t1, fontFamily: T.font, cursor: "pointer", whiteSpace: "nowrap",
                                }}
                            >
                                {label(n)}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

/** First / Prev / "Page X of Y" / Next / Last. First+Last hide on mobile. */
export function PaginationBar({ page, totalPages, onChange }: {
    page: number;
    totalPages: number;
    onChange: (page: number) => void;
}) {
    const isDesktop = useIsDesktop();
    const btn = (label: string, icon: string, target: number, disabled: boolean, compactHide = false) => (
        <button
            key={label}
            onClick={() => !disabled && onChange(target)}
            disabled={disabled}
            title={label}
            style={{
                display: compactHide && !isDesktop ? "none" : "flex", alignItems: "center", gap: 4,
                padding: isDesktop ? "6px 10px" : "6px 8px", borderRadius: 7,
                border: `1px solid ${T.cardBorder}`, background: T.surface,
                color: disabled ? T.t3 : T.t2, cursor: disabled ? "default" : "pointer",
                opacity: disabled ? 0.5 : 1, fontSize: 12, fontWeight: 600,
            }}
        >
            <Icon name={icon} size={16} color={disabled ? T.t3 : T.t2} />
            {isDesktop && <span>{label}</span>}
        </button>
    );
    return (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {btn("First", "first_page", 1, page <= 1, true)}
            {btn("Prev", "chevron_left", page - 1, page <= 1)}
            <span style={{ fontSize: 12, fontWeight: 600, color: T.t2, padding: "0 8px", whiteSpace: "nowrap" }}>
                Page {page.toLocaleString()} of {Math.max(1, totalPages).toLocaleString()}
            </span>
            {btn("Next", "chevron_right", page + 1, page >= totalPages)}
            {btn("Last", "last_page", totalPages, page >= totalPages, true)}
        </div>
    );
}

/* ─── Horizontal proportional bar row (segment AOV, complaints, channels) ──── */
export function BarRow({ label, value, display, pct, color, sub }: any) {
    return (
        <div style={{ marginBottom: 10 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4, gap: 8 }}>
                <span style={{ fontSize: 12, color: T.t2, display: "flex", alignItems: "center", gap: 6, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: color, flexShrink: 0 }} />
                    {label}
                </span>
                <span style={{ fontSize: 12, fontWeight: 600, color: T.t1, whiteSpace: "nowrap" }}>{display ?? value}{sub && <span style={{ fontSize: 10, color: T.t3, fontWeight: 400, marginLeft: 6 }}>{sub}</span>}</span>
            </div>
            <div style={{ height: 8, background: T.hover, borderRadius: 4, overflow: "hidden" }}>
                <div style={{ width: `${Math.max(2, Math.min(100, pct))}%`, height: "100%", background: color, borderRadius: 4, transition: "width 0.3s" }} />
            </div>
        </div>
    );
}

/**
 * The search field every customer table uses - the Directory, each drill and, since
 * QA #57, the customer page's Order Log. One component, so they cannot drift; only
 * the placeholder says what each one searches.
 */
export function CustomerSearchBox({ value, onChange, placeholder = "Search name or last 4 digits" }: { value: string; onChange: (v: string) => void; placeholder?: string }) {
    const isDesktop = useIsDesktop();
    return (
        <div style={{
            display: "flex", alignItems: "center", gap: 6, padding: "6px 10px", background: T.hover, borderRadius: 8,
            flex: isDesktop ? "0 0 auto" : "1 1 100%", minWidth: 0,
        }}>
            <Icon name="search" size={15} color={T.t3} />
            <input placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)}
                style={{ border: "none", outline: "none", background: "transparent", fontSize: 12, color: T.t1, fontFamily: T.font, width: isDesktop ? 200 : "100%", minWidth: 0, flex: isDesktop ? undefined : 1 }} />
            {value && <Icon name="close" size={14} color={T.t3} onClick={() => onChange("")} />}
        </div>
    );
}
